/**
 * Full & Final settlement on the payroll desk (ADR 0015) — `/admin/payroll/operations/fnf`.
 *
 *     initiated → clearance_review → calculated → submitted → approved → payment_processing → paid
 *                                                                    (+ rejected, cancelled)
 * The stages, edges, item shape, totals and gate rules are `../fnf-settlement.ts`; this file declares
 * the routes. CONTRACT FIRST (CLAUDE.md invariant 8): nothing implements them yet.
 *
 * WHO DOES WHAT, ONE PERMISSION PER ROUTE.
 *   - `fnf:read`              the desk sees exits, settlements, versions and clearances.
 *   - `fnf:execute`           HR / payroll initiate, calculate, submit, cancel and publish the statement.
 *   - `fnf:approve`           the OWNER approves or rejects the exact version + digest. It is
 *                             self-excluded (`can.ts`), and the handler also refuses the person who
 *                             calculated or submitted that version (maker-checker): 409.
 *   - `fnf.clearance:manage`  HR defines the clearance departments and may WAIVE one, with a reason.
 *   - signed-in (`null`)      a clearance OWNER records their own department's clearance — and only
 *                             theirs; an employee reads their own settlement and published statement.
 *                             These are assignment-based: the handler decides from the record, never
 *                             from a role, and answers 404 for a record the caller has no part in.
 *
 * EVERY WRITE TAKES `Idempotency-Key` (invariant 10), AND EVERY WRITE THAT MOVES A SETTLEMENT (or a
 * clearance) TAKES ITS `expectedVersion`: a record that moved on is a 409. Submission and decision also
 * name the calculation `versionId` and its `digest`, so an approval binds to exactly what was seen.
 *
 * PAYMENT IS NOT HERE. Approval creates an `fnf_settlement` obligation in the payment service
 * (`../payroll-payments.ts`); batching, authorisation, transmission and reconciliation go through the
 * payments contract, and the settlement's stage follows its obligation. There is deliberately no
 * mark-paid route: approval never marks paid, and "paid" is a confirmed reconciliation.
 *
 * THE LEGACY ROUTES STAY. `/employees/fnf-settlements` in `employee.contract.ts` is unchanged, for
 * compatibility (web `FnfSettlementPage`, mobile `admin-fnf`, MyDetails):
 *   - `createFnfSettlement`   still records a manual (`basis = legacy_manual`) settlement, `submitted`.
 *   - `listFnfSettlements` / `getMyFnfSettlements`  read every settlement through the four-value legacy
 *                             `status`, kept correct by `stageToLegacyStatus` for new-path rows too.
 *   - `approveFnfSettlement` / `rejectFnfSettlement`  decide LEGACY workflow instances
 *                             (`fnf_settlement`); a new-path settlement is decided here, bound to a version
 *                             (`fnf_settlement_version`).
 *   - `markFnfSettlementPaid` is the legacy manual path; a new-path settlement is paid only through its
 *                             payment obligation.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  FNF_CLEARANCE_DEPARTMENTS,
  FNF_CLEARANCE_GATES,
  FNF_CLEARANCE_OWNER_RULES,
  FNF_CLEARANCE_STATUSES,
  FNF_ITEM_KINDS,
  FNF_ITEM_ORIGINS,
  FNF_LEGACY_STATUSES,
  FNF_PROBLEM_CODES,
  FNF_SECTIONS,
  FNF_SETTLEMENT_BASES,
  FNF_STAGES,
  FNF_VERSION_STATUSES,
} from '../fnf-settlement.js';
import { PAYMENT_OBLIGATION_STATES } from '../payroll-payments.js';
import {
  currencySchema,
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  exitTypeSchema,
  idempotencyKeySchema,
  instantSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });
const settlementParams = z.object({ settlementId: uuidSchema });
const versionParams = z.object({ settlementId: uuidSchema, versionId: uuidSchema });
const clearanceParams = z.object({ settlementId: uuidSchema, clearanceId: uuidSchema });
const expectedVersionSchema = z.number().int().positive();

/** A line amount: never negative, the kind carries the sign. */
const nonNegativeDecimalSchema = decimalStringSchema.refine(
  (v) => !v.startsWith('-'),
  'Must be zero or greater',
);
const positiveDecimalSchema = decimalStringSchema.refine(
  (v) => !v.startsWith('-') && /[1-9]/.test(v),
  'Must be greater than zero',
);

export const fnfStageSchema = z.enum(FNF_STAGES);
export const fnfLegacyStatusSchema = z.enum(FNF_LEGACY_STATUSES);
export const fnfSettlementBasisSchema = z.enum(FNF_SETTLEMENT_BASES);
export const fnfSectionSchema = z.enum(FNF_SECTIONS);
export const fnfVersionStatusSchema = z.enum(FNF_VERSION_STATUSES);
export const fnfClearanceDepartmentSchema = z.enum(FNF_CLEARANCE_DEPARTMENTS);
export const fnfClearanceGateSchema = z.enum(FNF_CLEARANCE_GATES);
export const fnfClearanceOwnerRuleSchema = z.enum(FNF_CLEARANCE_OWNER_RULES);
export const fnfClearanceStatusSchema = z.enum(FNF_CLEARANCE_STATUSES);
const obligationStateSchema = z.enum(PAYMENT_OBLIGATION_STATES);

// ---------------------------------------------------------------------------
// Exits
// ---------------------------------------------------------------------------

/** An employment with exit facts — what a settlement is initiated FROM (never a second separation record). */
export const fnfExitRowSchema = z.object({
  employmentId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string(),
  designation: z.string().nullable(),
  department: z.string().nullable(),
  legalEntityId: uuidSchema,
  exitType: exitTypeSchema.nullable(),
  resignationRequestId: uuidSchema.nullable(),
  noticeStartOn: dateOnlySchema.nullable(),
  lastWorkingDay: dateOnlySchema.nullable(),
  noticePeriodDays: z.number().int().nonnegative().nullable(),
  /** The ACTIVE settlement for this exit, if any. */
  settlementId: uuidSchema.nullable(),
  stage: fnfStageSchema.nullable(),
});
export type FnfExitRow = z.infer<typeof fnfExitRowSchema>;

export const listFnfExitsQuerySchema = z.object({
  q: z.string().trim().min(1).max(200).optional(),
  legalEntityId: uuidSchema.optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().min(5).max(100).default(25),
});

export const fnfExitListSchema = z.object({
  items: z.array(fnfExitRowSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});

// ---------------------------------------------------------------------------
// Calculation versions
// ---------------------------------------------------------------------------

const sourceRefSchema = z.object({ type: z.string().min(1).max(64), id: z.string().min(1) });

/** The wire shape of `FnfCalculationItem`. */
export const fnfCalculationItemSchema = z.object({
  section: fnfSectionSchema,
  code: z.string().min(1).max(64),
  label: z.string(),
  kind: z.enum(FNF_ITEM_KINDS),
  amount: nonNegativeDecimalSchema,
  currency: currencySchema,
  origin: z.enum(FNF_ITEM_ORIGINS),
  manual: z
    .object({
      reason: z.string(),
      authorisedByPersonId: uuidSchema,
      authorisedByName: z.string(),
    })
    .nullable(),
  formula: z
    .object({
      expression: z.string(),
      inputs: z.array(
        z.object({
          name: z.string(),
          value: z.string(),
          source: z.object({ type: z.string(), id: z.string().nullable(), label: z.string() }),
        }),
      ),
    })
    .nullable(),
  ruleRef: z.object({ ruleCode: z.string(), ruleVersionId: uuidSchema }).nullable(),
  sourceRefs: z.array(sourceRefSchema),
  taxTreatment: z
    .object({
      exemptAmount: decimalStringSchema,
      taxableAmount: decimalStringSchema,
      basis: z.string(),
    })
    .nullable(),
  coverage: z
    .object({
      fromDate: dateOnlySchema,
      toDate: dateOnlySchema,
      coveredByRunIds: z.array(uuidSchema),
    })
    .nullable(),
});
export type FnfCalculationItemDto = z.infer<typeof fnfCalculationItemSchema>;

export const fnfProblemSchema = z.object({
  code: z.enum(FNF_PROBLEM_CODES),
  section: fnfSectionSchema.nullable(),
  message: z.string(),
});

export const fnfTotalsSchema = z.object({
  grossEarnings: decimalStringSchema,
  totalDeductions: decimalStringSchema,
  /** Negative means the employee owes the company. */
  netPayable: decimalStringSchema,
});

export const FNF_NOTICE_DECISIONS = ['as_policy', 'waive', 'recover'] as const;

const manualItemInputSchema = z.object({
  section: fnfSectionSchema,
  code: z
    .string()
    .trim()
    .regex(/^[A-Z][A-Z0-9_]{1,63}$/, 'Use an UPPER_SNAKE code'),
  label: z.string().trim().min(1).max(200),
  kind: z.enum(FNF_ITEM_KINDS),
  amount: nonNegativeDecimalSchema,
  reason: z.string().trim().min(10).max(1000),
});

/**
 * The financial inputs a person decides; everything else (compensation, attendance, balances, rules,
 * service, clearance recoveries) is read server-side. Every calculation is a NEW version.
 */
export const fnfCalculationInputsSchema = z.object({
  noticeDecision: z
    .object({
      type: z.enum(FNF_NOTICE_DECISIONS),
      reason: z.string().trim().min(10).max(1000).optional(),
    })
    .superRefine((d, ctx) => {
      if (d.type !== 'as_policy' && !d.reason) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['reason'],
          message: `Say why the notice is being ${d.type === 'waive' ? 'waived' : 'recovered'}`,
        });
      }
    }),
  /** Leave types to encash; omitted means every type the policy makes encashable. */
  leaveTypeIds: z.array(uuidSchema).max(50).optional(),
  manualItems: z.array(manualItemInputSchema).max(20).optional(),
  bonusAmount: nonNegativeDecimalSchema.optional(),
});
export type FnfCalculationInputs = z.infer<typeof fnfCalculationInputsSchema>;

export const calculateFnfSettlementBodySchema = z.object({
  expectedVersion: expectedVersionSchema,
  inputs: fnfCalculationInputsSchema,
});
export type CalculateFnfSettlementBody = z.infer<typeof calculateFnfSettlementBodySchema>;

export const fnfVersionSummarySchema = z.object({
  id: uuidSchema,
  versionNo: z.number().int().positive(),
  status: fnfVersionStatusSchema,
  netPayable: decimalStringSchema,
  currency: currencySchema,
  calculatedAt: instantSchema,
  calculatedByName: z.string().nullable(),
  supersededAt: instantSchema.nullable(),
});
export type FnfVersionSummary = z.infer<typeof fnfVersionSummarySchema>;

export const fnfVersionDetailSchema = z.object({
  id: uuidSchema,
  settlementId: uuidSchema,
  versionNo: z.number().int().positive(),
  status: fnfVersionStatusSchema,
  problems: z.array(fnfProblemSchema),
  /** Explanations that are not problems (N12): an ineligible gratuity, nothing to encash, a notice fully served. */
  notes: z.array(z.string()).optional(),
  items: z.array(fnfCalculationItemSchema),
  totals: fnfTotalsSchema,
  currency: currencySchema,
  /** The digest an approval binds to (server-computed over items, totals and inputs). */
  digest: z.string(),
  inputs: fnfCalculationInputsSchema,
  calculatedAt: instantSchema,
  calculatedByName: z.string().nullable(),
  supersededAt: instantSchema.nullable(),
  engineVersion: z.string(),
});
export type FnfVersionDetail = z.infer<typeof fnfVersionDetailSchema>;

// ---------------------------------------------------------------------------
// Clearances
// ---------------------------------------------------------------------------

export const fnfClearanceSchema = z.object({
  id: uuidSchema,
  settlementId: uuidSchema,
  department: fnfClearanceDepartmentSchema,
  label: z.string(),
  ownerRule: fnfClearanceOwnerRuleSchema,
  /** Who it resolved to when the settlement was initiated. */
  owner: z.object({
    personId: uuidSchema.nullable(),
    name: z.string().nullable(),
    role: z.string().nullable(),
  }),
  gate: fnfClearanceGateSchema,
  required: z.boolean(),
  status: fnfClearanceStatusSchema,
  note: z.string().nullable(),
  recoveryAmount: decimalStringSchema.nullable(),
  currency: currencySchema,
  evidence: z.array(z.object({ label: z.string(), documentKey: z.string() })),
  decidedByName: z.string().nullable(),
  decidedAt: instantSchema.nullable(),
  waivedReason: z.string().nullable(),
  /** The clearance's own optimistic-concurrency version. */
  version: z.number().int().positive(),
});
export type FnfClearance = z.infer<typeof fnfClearanceSchema>;

/** A clearance in someone's queue, with just enough of the exit to act on it. */
export const myFnfClearanceSchema = fnfClearanceSchema.extend({
  /** The leaver (a Person id), so the IT and finance owners can see their open company assets. */
  personId: uuidSchema.optional(),
  personName: z.string(),
  employeeCode: z.string(),
  lastWorkingDay: dateOnlySchema.nullable(),
  stage: fnfStageSchema,
  /** Why it is mine: I am the named person, the exiting employee's manager, or I hold the role. */
  assignedVia: fnfClearanceOwnerRuleSchema,
});

export const listMyFnfClearancesQuerySchema = z.object({
  status: fnfClearanceStatusSchema.optional(),
});

export const recordFnfClearanceBodySchema = z
  .object({
    expectedVersion: expectedVersionSchema,
    status: z.enum(['cleared', 'cleared_with_recovery', 'blocked']),
    note: z.string().trim().min(5).max(2000),
    recoveryAmount: positiveDecimalSchema.optional(),
    evidence: z
      .array(
        z.object({
          label: z.string().trim().min(1).max(200),
          documentKey: z.string().trim().min(1).max(500),
        }),
      )
      .max(5)
      .optional(),
  })
  .superRefine((b, ctx) => {
    const withRecovery = b.status === 'cleared_with_recovery';
    if (withRecovery && b.recoveryAmount === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['recoveryAmount'],
        message: 'Say how much is to be recovered',
      });
    }
    if (!withRecovery && b.recoveryAmount !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['recoveryAmount'],
        message: "A recovery amount is recorded only with 'cleared_with_recovery'",
      });
    }
  });
export type RecordFnfClearanceBody = z.infer<typeof recordFnfClearanceBodySchema>;

export const waiveFnfClearanceBodySchema = z.object({
  expectedVersion: expectedVersionSchema,
  reason: z.string().trim().min(10).max(2000),
});

export const fnfClearanceTemplateSchema = z
  .object({
    department: fnfClearanceDepartmentSchema,
    label: z.string().trim().min(1).max(120),
    ownerRule: fnfClearanceOwnerRuleSchema,
    /** A role key for `role`, a person id for `person`, `null` for `reporting_manager`. */
    ownerRoleOrPersonId: z.string().trim().min(1).max(64).nullable(),
    gate: fnfClearanceGateSchema,
    required: z.boolean(),
    sortOrder: z.number().int().min(0).max(1000),
  })
  .superRefine((t, ctx) => {
    if (t.ownerRule === 'reporting_manager' && t.ownerRoleOrPersonId !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ownerRoleOrPersonId'],
        message: 'The reporting manager is resolved per exit; name no one',
      });
    }
    if (t.ownerRule !== 'reporting_manager' && t.ownerRoleOrPersonId === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ownerRoleOrPersonId'],
        message: `Say which ${t.ownerRule} owns this clearance`,
      });
    }
    if (t.ownerRule === 'person' && !uuidSchema.safeParse(t.ownerRoleOrPersonId).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ownerRoleOrPersonId'],
        message: 'A person owner is a person id',
      });
    }
  });
export type FnfClearanceTemplate = z.infer<typeof fnfClearanceTemplateSchema>;

export const fnfClearanceTemplateListSchema = z.object({
  items: z.array(
    z.object({
      id: uuidSchema,
      /** `null` is the organisation default; a legal entity's own list replaces it for that entity. */
      legalEntityId: uuidSchema.nullable(),
      department: fnfClearanceDepartmentSchema,
      label: z.string(),
      ownerRule: fnfClearanceOwnerRuleSchema,
      ownerRoleOrPersonId: z.string().nullable(),
      ownerName: z.string().nullable(),
      gate: fnfClearanceGateSchema,
      required: z.boolean(),
      sortOrder: z.number().int(),
    }),
  ),
});

/** Replaces the whole list for that scope. Settlements already initiated keep their clearances. */
export const saveFnfClearanceTemplatesBodySchema = z
  .object({
    legalEntityId: uuidSchema.nullable(),
    templates: z.array(fnfClearanceTemplateSchema).min(1).max(20),
  })
  .superRefine((b, ctx) => {
    const labels = b.templates.map((t) => t.label.toLowerCase());
    labels.forEach((l, i) => {
      if (labels.indexOf(l) !== i) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['templates', i, 'label'],
          message: 'Each clearance needs a distinct label',
        });
      }
    });
  });

// ---------------------------------------------------------------------------
// Settlements
// ---------------------------------------------------------------------------

const employmentFactsSchema = z.object({
  employmentId: uuidSchema,
  /** The leaver's person id — what the Assets exit-recovery calls target. */
  personId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string(),
  designation: z.string().nullable(),
  department: z.string().nullable(),
  legalEntityId: uuidSchema,
  exitType: exitTypeSchema.nullable(),
  noticeStartOn: dateOnlySchema.nullable(),
  lastWorkingDay: dateOnlySchema.nullable(),
});

const approvalSummarySchema = z.object({
  state: z.enum(['pending', 'approved', 'rejected', 'invalidated']),
  byName: z.string().nullable(),
  at: instantSchema.nullable(),
});

export const fnfSettlementSummarySchema = z.object({
  id: uuidSchema,
  employment: employmentFactsSchema,
  basis: fnfSettlementBasisSchema,
  stage: fnfStageSchema,
  legacyStatus: fnfLegacyStatusSchema,
  currentVersion: z
    .object({
      id: uuidSchema,
      versionNo: z.number().int().positive(),
      status: fnfVersionStatusSchema,
      netPayable: decimalStringSchema,
      currency: currencySchema,
    })
    .nullable(),
  clearances: z.object({
    total: z.number().int().nonnegative(),
    cleared: z.number().int().nonnegative(),
    /** Clearances holding submission back now (`clearanceGateVerdict`). */
    blocking: z.number().int().nonnegative(),
  }),
  approval: approvalSummarySchema.nullable(),
  payment: z.object({ obligationId: uuidSchema, state: obligationStateSchema }).nullable(),
  updatedAt: instantSchema,
});
export type FnfSettlementSummary = z.infer<typeof fnfSettlementSummarySchema>;

export const listFnfSettlementsV2QuerySchema = paginationQuerySchema.extend({
  /** One stage, or `active` (not paid / cancelled) or `archived` (paid / cancelled). */
  stage: z.enum([...FNF_STAGES, 'active', 'archived']).optional(),
  q: z.string().trim().min(1).max(200).optional(),
  legalEntityId: uuidSchema.optional(),
});

const actionSchema = z.object({
  allowed: z.boolean(),
  /** Why not, in words for the screen; `null` when allowed. */
  reason: z.string().nullable(),
});

export const fnfSettlementDetailSchema = fnfSettlementSummarySchema.extend({
  resignationRequestId: uuidSchema.nullable(),
  noticePeriodDays: z.number().int().nonnegative().nullable(),
  cancelledReason: z.string().nullable(),
  clearanceItems: z.array(fnfClearanceSchema),
  versions: z.array(fnfVersionSummarySchema),
  approvalTrail: z.array(
    z.object({
      action: z.enum(['submitted', 'approved', 'rejected', 'invalidated', 'cancelled']),
      versionNo: z.number().int().positive().nullable(),
      byName: z.string().nullable(),
      at: instantSchema,
      comment: z.string().nullable(),
    }),
  ),
  paymentDetail: z
    .object({
      obligationId: uuidSchema,
      state: obligationStateSchema,
      attempts: z.number().int().nonnegative(),
      paidAt: instantSchema.nullable(),
      bankReference: z.string().nullable(),
    })
    .nullable(),
  statement: z
    .object({
      publishedAt: instantSchema,
      publishedByName: z.string().nullable(),
      versionNo: z.number().int().positive(),
      documentVersion: z.number().int().positive(),
    })
    .nullable(),
  actions: z.object({
    canCalculate: actionSchema,
    canSubmit: actionSchema,
    canApprove: actionSchema,
    canPublishStatement: actionSchema,
    canCancel: actionSchema,
  }),
  /** The settlement's optimistic-concurrency version: send it back as `expectedVersion`. */
  version: z.number().int().positive(),
});
export type FnfSettlementDetail = z.infer<typeof fnfSettlementDetailSchema>;

export const initiateFnfSettlementBodySchema = z.object({
  employmentId: uuidSchema,
  resignationRequestId: uuidSchema.optional(),
});

export const submitFnfSettlementBodySchema = z.object({
  expectedVersion: expectedVersionSchema,
  versionId: uuidSchema,
  digest: z.string().min(1).max(128),
});

export const decideFnfSettlementV2BodySchema = z
  .object({
    expectedVersion: expectedVersionSchema,
    versionId: uuidSchema,
    digest: z.string().min(1).max(128),
    decision: z.enum(['approved', 'rejected']),
    comment: z.string().trim().max(2000).optional(),
  })
  .superRefine((b, ctx) => {
    if (b.decision === 'rejected' && !b.comment) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['comment'],
        message: 'Say why the settlement is rejected',
      });
    }
  });

export const cancelFnfSettlementBodySchema = z.object({
  expectedVersion: expectedVersionSchema,
  reason: z.string().trim().min(10).max(2000),
});

export const publishFnfStatementBodySchema = z.object({
  expectedVersion: expectedVersionSchema,
  versionId: uuidSchema,
});

export const fnfDownloadLinkSchema = z.object({
  url: z.string().url(),
  expiresAt: instantSchema,
  fileName: z.string(),
});

// ---------------------------------------------------------------------------
// The employee's own view
// ---------------------------------------------------------------------------

/** What an employee sees of their own statement: the PUBLISHED version's lines, not its sources. */
export const myFnfStatementItemSchema = fnfCalculationItemSchema
  .pick({
    section: true,
    code: true,
    label: true,
    kind: true,
    amount: true,
    currency: true,
  })
  .extend({ formula: z.string().nullable() });

export const myFnfSettlementSummarySchema = z.object({
  id: uuidSchema,
  stage: fnfStageSchema,
  lastWorkingDay: dateOnlySchema.nullable(),
  statementPublishedAt: instantSchema.nullable(),
  /** Published statements only; `null` before publication. */
  netPayable: decimalStringSchema.nullable(),
  currency: currencySchema.nullable(),
  paymentState: obligationStateSchema.nullable(),
});

export const myFnfSettlementListSchema = z.object({ items: z.array(myFnfSettlementSummarySchema) });

export const myFnfSettlementSchema = myFnfSettlementSummarySchema.extend({
  statement: z
    .object({
      versionNo: z.number().int().positive(),
      items: z.array(myFnfStatementItemSchema),
      totals: fnfTotalsSchema,
    })
    .nullable(),
  paidAt: instantSchema.nullable(),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

const readErrors = { 401: errorBodySchema, 403: errorBodySchema } as const;
const writeErrors = {
  400: errorBodySchema,
  401: errorBodySchema,
  403: errorBodySchema,
  404: errorBodySchema,
  /** The record moved on (`expectedVersion`), the version or digest is stale, or maker-checker refused it. */
  409: errorBodySchema,
  /** The settlement is not in a stage that allows this. */
  412: errorBodySchema,
} as const;

export const payrollFnfContract = c.router(
  {
    listExits: {
      method: 'GET',
      path: '/exits',
      query: listFnfExitsQuerySchema,
      responses: { 200: fnfExitListSchema, ...readErrors },
      summary: 'Employments with an exit recorded, and their settlement if one exists',
      metadata: routeMeta('fnf:read'),
    },

    listSettlements: {
      method: 'GET',
      path: '/settlements',
      query: listFnfSettlementsV2QuerySchema,
      responses: { 200: paginatedSchema(fnfSettlementSummarySchema), ...readErrors },
      summary: 'Full & Final settlements by stage',
      metadata: routeMeta('fnf:read', { salaryPin: 'others' }),
    },

    initiateSettlement: {
      method: 'POST',
      path: '/settlements',
      headers: idempotencyHeadersSchema,
      body: initiateFnfSettlementBodySchema,
      responses: {
        201: fnfSettlementDetailSchema,
        ...writeErrors,
        /** The employment has no exit recorded. */
        422: errorBodySchema,
      },
      summary: 'Start a Full & Final settlement from a recorded exit',
      metadata: routeMeta('fnf:execute', { salaryPin: 'others' }),
    },

    getSettlement: {
      method: 'GET',
      path: '/settlements/:settlementId',
      pathParams: settlementParams,
      responses: { 200: fnfSettlementDetailSchema, ...readErrors, 404: errorBodySchema },
      summary: 'A settlement with its clearances, versions, approval and payment',
      metadata: routeMeta('fnf:read', { salaryPin: 'others' }),
    },

    calculateSettlement: {
      method: 'POST',
      path: '/settlements/:settlementId/calculate',
      pathParams: settlementParams,
      headers: idempotencyHeadersSchema,
      body: calculateFnfSettlementBodySchema,
      responses: { 200: fnfVersionDetailSchema, ...writeErrors },
      summary: 'Calculate a new settlement version (voids any submission or approval)',
      metadata: routeMeta('fnf:execute', { salaryPin: 'others' }),
    },

    listVersions: {
      method: 'GET',
      path: '/settlements/:settlementId/versions',
      pathParams: settlementParams,
      responses: {
        200: z.object({ items: z.array(fnfVersionSummarySchema) }),
        ...readErrors,
        404: errorBodySchema,
      },
      summary: "A settlement's calculation versions",
      metadata: routeMeta('fnf:read', { salaryPin: 'others' }),
    },

    getVersion: {
      method: 'GET',
      path: '/settlements/:settlementId/versions/:versionId',
      pathParams: versionParams,
      responses: { 200: fnfVersionDetailSchema, ...readErrors, 404: errorBodySchema },
      summary: 'One calculation version with its items, formulas and totals',
      metadata: routeMeta('fnf:read', { salaryPin: 'others' }),
    },

    submitSettlement: {
      method: 'POST',
      path: '/settlements/:settlementId/submit',
      pathParams: settlementParams,
      headers: idempotencyHeadersSchema,
      body: submitFnfSettlementBodySchema,
      responses: { 200: fnfSettlementDetailSchema, ...writeErrors },
      summary: 'Submit a ready calculation version for approval',
      metadata: routeMeta('fnf:execute', { salaryPin: 'others' }),
    },

    decideSettlement: {
      method: 'POST',
      path: '/settlements/:settlementId/decision',
      pathParams: settlementParams,
      headers: idempotencyHeadersSchema,
      body: decideFnfSettlementV2BodySchema,
      responses: { 200: fnfSettlementDetailSchema, ...writeErrors },
      summary: 'Approve or reject the exact submitted settlement version',
      metadata: routeMeta('fnf:approve', { salaryPin: 'others' }),
    },

    cancelSettlement: {
      method: 'POST',
      path: '/settlements/:settlementId/cancel',
      pathParams: settlementParams,
      headers: idempotencyHeadersSchema,
      body: cancelFnfSettlementBodySchema,
      responses: { 200: fnfSettlementDetailSchema, ...writeErrors },
      summary: 'Cancel a settlement before approval',
      metadata: routeMeta('fnf:execute', { salaryPin: 'others' }),
    },

    publishStatement: {
      method: 'POST',
      path: '/settlements/:settlementId/statement/publish',
      pathParams: settlementParams,
      headers: idempotencyHeadersSchema,
      body: publishFnfStatementBodySchema,
      responses: { 200: fnfSettlementDetailSchema, ...writeErrors },
      summary: "Publish an approved settlement's statement to the employee",
      metadata: routeMeta('fnf:execute', { salaryPin: 'others' }),
    },

    getStatementDownload: {
      method: 'GET',
      path: '/settlements/:settlementId/statement/download',
      pathParams: settlementParams,
      responses: { 200: fnfDownloadLinkSchema, ...readErrors, 404: errorBodySchema },
      summary: 'A short-lived private link to the settlement statement',
      metadata: routeMeta('fnf:read', { ai: 'hidden', salaryPin: 'others' }),
    },

    listClearanceTemplates: {
      method: 'GET',
      path: '/clearance-templates',
      query: z.object({ legalEntityId: uuidSchema.optional() }),
      responses: { 200: fnfClearanceTemplateListSchema, ...readErrors },
      summary: 'The exit clearance departments, owners and gates',
      metadata: routeMeta('fnf:read'),
    },

    saveClearanceTemplates: {
      method: 'PUT',
      path: '/clearance-templates',
      headers: idempotencyHeadersSchema,
      body: saveFnfClearanceTemplatesBodySchema,
      responses: {
        200: fnfClearanceTemplateListSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Define which departments clear an exit, who owns each and what it gates',
      metadata: routeMeta('fnf.clearance:manage'),
    },

    listMyClearances: {
      method: 'GET',
      path: '/clearances/mine',
      query: listMyFnfClearancesQuerySchema,
      responses: { 200: z.object({ items: z.array(myFnfClearanceSchema) }), 401: errorBodySchema },
      summary: 'Exit clearances assigned to me',
      // Assignment-based: as the named person, the exiting employee's reporting manager, or a role holder.
      metadata: routeMeta(null),
    },

    recordClearance: {
      method: 'POST',
      path: '/settlements/:settlementId/clearances/:clearanceId/decision',
      pathParams: clearanceParams,
      headers: idempotencyHeadersSchema,
      body: recordFnfClearanceBodySchema,
      responses: { 200: fnfClearanceSchema, ...writeErrors },
      summary: "Record my department's clearance of an exit",
      // Signed-in only at the edge; the handler allows ONLY the clearance's assigned owner (404 for
      // anyone else). Nobody records another department's clearance — HR waives, it does not clear.
      metadata: routeMeta(null),
    },

    waiveClearance: {
      method: 'POST',
      path: '/settlements/:settlementId/clearances/:clearanceId/waive',
      pathParams: clearanceParams,
      headers: idempotencyHeadersSchema,
      body: waiveFnfClearanceBodySchema,
      responses: { 200: fnfClearanceSchema, ...writeErrors },
      summary: 'Waive a clearance, with a reason',
      metadata: routeMeta('fnf.clearance:manage'),
    },

    listPendingApprovals: {
      method: 'GET',
      path: '/approvals',
      query: paginationQuerySchema,
      responses: { 200: paginatedSchema(fnfSettlementSummarySchema), ...readErrors },
      summary: 'Settlements submitted and awaiting my approval',
      metadata: routeMeta('fnf:approve', { salaryPin: 'others' }),
    },

    listMySettlements: {
      method: 'GET',
      path: '/mine',
      responses: { 200: myFnfSettlementListSchema, 401: errorBodySchema },
      summary: 'My Full & Final settlement and its payment status',
      // No own-scope permission exists for F&F: the handler restricts to the caller's own employments,
      // and shows amounts only from a PUBLISHED statement.
      metadata: routeMeta(null),
    },

    getMySettlement: {
      method: 'GET',
      path: '/mine/:settlementId',
      pathParams: settlementParams,
      responses: { 200: myFnfSettlementSchema, 401: errorBodySchema, 404: errorBodySchema },
      summary: 'My settlement statement, once published',
      // 404 for a settlement that is not the caller's own.
      metadata: routeMeta(null),
    },

    getMyStatementDownload: {
      method: 'GET',
      path: '/mine/:settlementId/statement/download',
      pathParams: settlementParams,
      responses: { 200: fnfDownloadLinkSchema, 401: errorBodySchema, 404: errorBodySchema },
      summary: 'A short-lived private link to my published settlement statement',
      metadata: routeMeta(null, { ai: 'hidden' }),
    },
  },
  { pathPrefix: '/payroll/fnf' },
);
